import { useEffect, useState } from 'react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';

interface Integration {
  id: string;
  provider: string;
  providerAccount: string;
  status: string;
  lastSyncedAt: string | null;
  lastError: string | null;
}

/**
 * Story 11.6 — integration status + reconnect. v1 shows the currently
 * registered providers, their sync timestamp, and a disconnect button.
 * The real OAuth connect flow (11.2 Gmail / 11.3 Outlook) needs
 * client IDs and a callback URL; once those land the "connect Gmail"
 * / "connect Outlook" buttons invoke the provider redirect and the
 * page already knows how to render the resulting row.
 */
export function IntegrationsPage() {
  const [items, setItems] = useState<Integration[]>([]);

  async function refresh() {
    try {
      const { data } = await apiClient.get<Integration[]>('/integrations');
      setItems(data);
    } catch {
      setItems([]);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function disconnect(id: string) {
    await apiClient.post(`/integrations/${id}/disconnect`, {});
    await refresh();
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Integraciones</h1>
      <p className="text-sm text-muted-foreground">
        Conecta tu bandeja de entrada para sincronizar correos y reuniones con
        oportunidades. Gmail y Outlook requieren credenciales OAuth
        configuradas por el equipo de plataforma.
      </p>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" disabled>
          Conectar Gmail (pendiente de configuración)
        </Button>
        <Button size="sm" variant="outline" disabled>
          Conectar Outlook (pendiente de configuración)
        </Button>
      </div>
      <ul className="divide-y rounded-lg border bg-card">
        {items.map((i) => (
          <li key={i.id} className="flex items-center justify-between px-4 py-3">
            <div>
              <div className="font-medium">
                {i.provider} · {i.providerAccount}
              </div>
              <div className="text-xs text-muted-foreground">
                {i.status}
                {i.lastSyncedAt && ` · última sync ${i.lastSyncedAt}`}
                {i.lastError && ` · ${i.lastError}`}
              </div>
            </div>
            {i.status === 'ACTIVE' && (
              <Button size="sm" variant="outline" onClick={() => disconnect(i.id)}>
                Desconectar
              </Button>
            )}
          </li>
        ))}
        {items.length === 0 && (
          <li className="px-4 py-6 text-center text-sm text-muted-foreground">
            Sin integraciones conectadas
          </li>
        )}
      </ul>
    </div>
  );
}
